import { useEffect, useState } from "react";
import { Modal } from "antd";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import PageContainer from "../../../components/page-container";
import Button from "../../../components/button/button";
import Message from "../../../components/toast-message";
import SwitchComponent from "../../../components/switch";
import {
	createPromptConfigMcpServer,
	updatePromptConfigMcpServer,
} from "../../../services/prompt-ai";
import { buildAgentNavigation } from "../../../consts/agent-nav-config";
import { useAgentConfigSource } from "../../../helpers/hooks/agent-config-source";
import SubAgentMissing from "../sub-agent-scope/sub-agent-missing";

// The backend never returns a stored token; it sends this sentinel instead.
// Echoing it back on save means "keep the token you already have".
const MASKED_TOKEN = "__stored__";

// The server name namespaces its tools as `<server>__<tool>` at runtime.
const NAME_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,47}$/;

const TRANSPORTS = [
	{ value: "http", label: "Streamable HTTP" },
	{ value: "sse", label: "SSE" },
];

// RFC 9110 token characters: what an HTTP header name may contain.
const HEADER_NAME_PATTERN = /^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/;

// Headers the runtime sets itself from the MCP spec; a custom entry with one of
// these names is ignored by the runtime, so it is rejected here instead.
const RESERVED_HEADERS = [
	"content-type",
	"mcp-protocol-version",
	"mcp-method",
	"mcp-name",
	"mcp-session-id",
];

const emptyHeaderRow = () => ({ key: "", value: "" });

const emptyForm = () => ({
	name: "",
	url: "",
	transport: "http",
	authorizationToken: "",
	headers: [],
	allowedTools: "",
	deactivate: false,
});

// Stored as an object ({ "X-Api-Key": "abc" }); edited as an ordered list of rows.
const headersToRows = (headers) =>
	headers && typeof headers === "object" && !Array.isArray(headers)
		? Object.entries(headers).map(([key, value]) => ({ key, value: String(value ?? "") }))
		: [];

const rowsToHeaders = (rows) =>
	(rows || []).reduce((acc, row) => {
		const key = (row.key || "").trim();
		if (key) acc[key] = (row.value || "").trim();
		return acc;
	}, {});

// ─── ServerCard ───────────────────────────────────────────────────────────────
function ServerCard({ server, onEdit, onDelete }) {
	const allowed = server.allowedTools || [];
	const headerCount = Object.keys(server.headers || {}).length;
	return (
		<div className="flex flex-row items-start justify-between border border-gray-200 rounded-lg bg-white px-4 py-3 hover:shadow-sm transition-shadow">
			<div className="flex-1 min-w-0 pr-4">
				<div className="flex items-center gap-2">
					<p className="text-sm font-semibold text-gray-800 font-mono truncate">
						{server.name}
					</p>
					<span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
						{TRANSPORTS.find((t) => t.value === server.transport)?.label || server.transport}
					</span>
					{server.deactivate && (
						<span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
							Disabled
						</span>
					)}
				</div>
				<p className="text-xs text-gray-500 mt-0.5 truncate">{server.url}</p>
				<p className="text-xs text-gray-400 mt-1">
					{allowed.length > 0 ? `${allowed.length} tool filter(s)` : "All tools exposed"}
					{server.hasAuthorizationToken ? " · authenticated" : ""}
					{headerCount > 0 ? ` · ${headerCount} custom header(s)` : ""}
				</p>
			</div>
			<div className="flex items-center gap-2 flex-shrink-0">
				<button
					type="button"
					onClick={() => onEdit(server)}
					className="p-1.5 text-gray-500 hover:text-primary transition-colors rounded"
					title="Edit MCP server"
				>
					<HiOutlinePencil className="h-4 w-4" />
				</button>
				<button
					type="button"
					onClick={() => onDelete(server)}
					className="p-1.5 text-gray-500 hover:text-red-500 transition-colors rounded"
					title="Delete MCP server"
				>
					<HiOutlineTrash className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}

// ─── DeleteConfirmModal ───────────────────────────────────────────────────────
function DeleteConfirmModal({ server, onConfirm, onCancel, processing }) {
	return (
		<Modal
			title={<span className="font-semibold text-gray-800">Delete MCP Server</span>}
			open={!!server}
			onCancel={onCancel}
			footer={
				<div className="flex justify-end gap-2">
					<Button text="Cancel" color="default" onClick={onCancel} disabled={processing} />
					<Button text="Delete" color="error" onClick={onConfirm} loading={processing} />
				</div>
			}
		>
			<p className="text-sm text-gray-600 mt-2">
				Are you sure you want to delete{" "}
				<span className="font-mono font-semibold">{server?.name}</span>? The agent will lose access to
				its tools. This action cannot be undone.
			</p>
		</Modal>
	);
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AgentMcpServers() {
	const {
		appId,
		subAgentId,
		subAgent,
		isSubAgent,
		scopeLoading,
		scopeError,
		mcp: mcpSource,
	} = useAgentConfigSource();

	const [servers, setServers] = useState([]);
	const [loadState, setLoadState] = useState({ processing: false });

	const [modalOpen, setModalOpen] = useState(false);
	const [editingServer, setEditingServer] = useState(null);
	const [editingForm, setEditingForm] = useState(emptyForm());

	const [deletingServer, setDeletingServer] = useState(null);
	const [deleteProcessing, setDeleteProcessing] = useState(false);

	async function fetchServers() {
		setLoadState({ processing: true });
		const data = await mcpSource.list();
		setServers(Array.isArray(data) ? data : []);
		setLoadState({ processing: false });
	}

	useEffect(() => {
		// in sub-agent scope the record must land before its toolData can be read
		if (appId && !scopeLoading) fetchServers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appId, subAgentId, scopeLoading]);

	const handleAddNew = () => {
		setEditingServer(null);
		setEditingForm(emptyForm());
		setModalOpen(true);
	};

	const handleEdit = (server) => {
		setEditingServer(server);
		setEditingForm({
			name: server.name || "",
			url: server.url || "",
			transport: server.transport || "http",
			// Carry the sentinel so an untouched field preserves the stored token.
			authorizationToken: server.hasAuthorizationToken ? MASKED_TOKEN : "",
			headers: headersToRows(server.headers),
			allowedTools: (server.allowedTools || []).join(", "),
			deactivate: !!server.deactivate,
		});
		setModalOpen(true);
	};

	const handleSaved = () => {
		setModalOpen(false);
		setEditingServer(null);
		fetchServers();
	};

	const handleDeleteConfirm = async () => {
		if (!deletingServer) return;
		setDeleteProcessing(true);
		await mcpSource.remove(deletingServer.id);
		setDeleteProcessing(false);
		setDeletingServer(null);
		fetchServers();
	};

	const navigation = buildAgentNavigation({ subAgentId });

	if (isSubAgent && !scopeLoading && scopeError) {
		return <SubAgentMissing navigation={navigation} />;
	}

	return (
		<PageContainer
			title="MCP"
			subtitle={
				isSubAgent
					? `MCP servers ${subAgent?.name || "this sub-agent"} can use`
					: "Connect MCP servers so the agent can use their tools"
			}
			navigationPosition="below-header"
			navigation={navigation}
			headerAction={<Button text="Add MCP Server" onClick={handleAddNew} />}
		>
			<div className="mt-6 px-1">
				{loadState.processing || scopeLoading ? (
					<div className="flex justify-center items-center py-16">
						<p className="text-sm text-gray-400">Loading MCP servers…</p>
					</div>
				) : servers.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-16 text-center">
						<p className="text-gray-500 text-sm font-medium">No MCP server connected yet</p>
						<p className="text-gray-400 text-xs mt-1 mb-4">
							Connect a Model Context Protocol server to give the agent its tools.
						</p>
						<Button text="Add your first MCP server" onClick={handleAddNew} />
					</div>
				) : (
					<div className="flex flex-col gap-3">
						{servers.map((server) => (
							<ServerCard
								key={server.id}
								server={server}
								onEdit={handleEdit}
								onDelete={setDeletingServer}
							/>
						))}
					</div>
				)}
			</div>

			<McpServerModal
				open={modalOpen}
				serverId={editingServer?.id}
				initialForm={editingForm}
				onClose={() => setModalOpen(false)}
				onSaved={handleSaved}
				appId={appId}
				onSave={isSubAgent ? mcpSource.save : undefined}
			/>

			<DeleteConfirmModal
				server={deletingServer}
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeletingServer(null)}
				processing={deleteProcessing}
			/>
		</PageContainer>
	);
}

// ─── McpServerModal ───────────────────────────────────────────────────────────
function McpServerModal({ open, serverId, initialForm, onClose, onSaved, appId, onSave }) {
	const isEdit = !!serverId;

	const [form, setForm] = useState(initialForm || emptyForm());
	const [saveState, setSaveState] = useState({ processing: false, failed: false });
	const [error, setError] = useState("");

	useEffect(() => {
		if (open) {
			setForm(initialForm || emptyForm());
			setSaveState({ processing: false, failed: false });
			setError("");
		}
	}, [open, serverId]);

	const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

	const validate = () => {
		const name = form.name.trim();
		if (!name) return "Server name is required.";
		if (!NAME_PATTERN.test(name) || name.includes("__"))
			return 'Name must be letters, numbers, underscore or hyphen, and cannot contain "__".';
		const url = form.url.trim();
		if (!url) return "Server URL is required.";
		if (!/^https?:\/\//i.test(url)) return "URL must start with http:// or https://";

		const seen = new Set();
		for (const row of form.headers) {
			const key = row.key.trim();
			const value = row.value.trim();
			if (!key && !value) continue; // an untouched blank row is simply dropped
			if (!key) return "Every header needs a name.";
			if (!HEADER_NAME_PATTERN.test(key))
				return `Header name "${key}" has characters that are not allowed.`;
			if (RESERVED_HEADERS.includes(key.toLowerCase()))
				return `Header "${key}" is set automatically and cannot be overridden.`;
			if (/[\r\n]/.test(value)) return `Header "${key}" cannot contain line breaks.`;
			if (seen.has(key.toLowerCase())) return `Header "${key}" is listed more than once.`;
			seen.add(key.toLowerCase());
		}
		return "";
	};

	const setHeaderRow = (index, patch) =>
		setForm((f) => ({
			...f,
			headers: f.headers.map((row, i) => (i === index ? { ...row, ...patch } : row)),
		}));
	const addHeaderRow = () =>
		setForm((f) => ({ ...f, headers: [...f.headers, emptyHeaderRow()] }));
	const removeHeaderRow = (index) =>
		setForm((f) => ({ ...f, headers: f.headers.filter((_, i) => i !== index) }));

	const handleSave = async () => {
		const validationError = validate();
		if (validationError) {
			setError(validationError);
			return;
		}
		setError("");
		setSaveState({ processing: true, failed: false });

		const payload = {
			name: form.name.trim(),
			url: form.url.trim(),
			transport: form.transport,
			// The sentinel tells the backend to keep the stored token.
			authorizationToken: form.authorizationToken,
			// Always sent, even when empty, so deleting every row clears the stored headers
			// (the backend merges partial updates over the existing record).
			headers: rowsToHeaders(form.headers),
			allowedTools: form.allowedTools
				.split(",")
				.map((t) => t.trim())
				.filter(Boolean),
			deactivate: !!form.deactivate,
		};

		// `onSave` lets a caller redirect persistence elsewhere (e.g. a
		// sub-agent's inline mcpServers map); without it the app-level API is used.
		const result = onSave
			? await onSave(payload, isEdit ? serverId : undefined)
			: isEdit
				? await updatePromptConfigMcpServer(appId, serverId, payload)
				: await createPromptConfigMcpServer(appId, payload);

		if (result?.status === 200 || result?.status === 201) {
			onSaved();
		} else {
			setSaveState({ processing: false, failed: true });
		}
	};

	const tokenIsStored = form.authorizationToken === MASKED_TOKEN;

	return (
		<Modal
			title={
				<span className="font-semibold text-gray-800">
					{isEdit ? "Edit MCP Server" : "Add MCP Server"}
				</span>
			}
			open={open}
			onCancel={onClose}
			width={660}
			destroyOnClose
			footer={
				<div className="flex justify-end gap-2">
					<Button text="Cancel" color="default" onClick={onClose} disabled={saveState.processing} />
					<Button
						text={isEdit ? "Update" : "Save"}
						onClick={handleSave}
						loading={saveState.processing}
					/>
				</div>
			}
		>
			<div className="flex flex-col gap-4 mt-4 max-h-[70vh] overflow-y-auto pr-1">
				{saveState.failed && (
					<Message text="Failed to save MCP server. Please try again." type="failure" />
				)}
				{error && <Message text={error} type="failure" />}

				{/* Name */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Server Name <span className="text-red-500">*</span>
					</label>
					<p className="text-xs text-gray-500 mb-1">
						Used to namespace its tools, e.g. <span className="font-mono">github</span> exposes{" "}
						<span className="font-mono">github__search</span>.
					</p>
					<input
						type="text"
						value={form.name}
						onChange={(e) => setField("name", e.target.value)}
						placeholder="github"
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>

				{/* Transport */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Transport
					</label>
					<select
						value={form.transport}
						onChange={(e) => setField("transport", e.target.value)}
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
					>
						{TRANSPORTS.map((t) => (
							<option key={t.value} value={t.value}>
								{t.label}
							</option>
						))}
					</select>
				</div>

				{/* URL */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Server URL <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						value={form.url}
						onChange={(e) => setField("url", e.target.value)}
						placeholder="https://example.com/mcp"
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>

				{/* Auth token */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Authorization Token
					</label>
					<p className="text-xs text-gray-500 mb-1">
						{tokenIsStored
							? "A token is saved. Clear this field and enter a new one to replace it."
							: "Sent as a Bearer token. Leave empty if the server needs no auth."}
					</p>
					<div className="flex gap-2">
						<input
							type="password"
							value={form.authorizationToken}
							onChange={(e) => setField("authorizationToken", e.target.value)}
							placeholder={tokenIsStored ? "" : "token"}
							className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
						/>
						{tokenIsStored && (
							<Button
								text="Replace"
								color="default"
								onClick={() => setField("authorizationToken", "")}
							/>
						)}
					</div>
				</div>

				{/* Custom headers */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Custom Headers
					</label>
					<p className="text-xs text-gray-500 mb-2">
						Extra HTTP headers sent with every request to this server, e.g.{" "}
						<span className="font-mono">X-Api-Key</span>. Protocol headers such as{" "}
						<span className="font-mono">MCP-Protocol-Version</span> are added automatically.
					</p>
					<div className="flex flex-col gap-2">
						{form.headers.map((row, index) => (
							<div key={index} className="flex gap-2 items-center">
								<input
									type="text"
									value={row.key}
									onChange={(e) => setHeaderRow(index, { key: e.target.value })}
									placeholder="Header"
									aria-label={`Header name ${index + 1}`}
									className="w-2/5 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 font-mono focus:outline-none focus:ring-2 focus:ring-primary"
								/>
								<input
									type="text"
									value={row.value}
									onChange={(e) => setHeaderRow(index, { value: e.target.value })}
									placeholder="Value"
									aria-label={`Header value ${index + 1}`}
									className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 font-mono focus:outline-none focus:ring-2 focus:ring-primary"
								/>
								<button
									type="button"
									onClick={() => removeHeaderRow(index)}
									className="p-1.5 text-gray-500 hover:text-red-500 transition-colors rounded"
									title="Remove header"
								>
									<HiOutlineTrash className="h-4 w-4" />
								</button>
							</div>
						))}
						<div>
							<Button text="Add Header" color="default" onClick={addHeaderRow} />
						</div>
					</div>
				</div>

				{/* Allowed tools */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Tool Filter
					</label>
					<p className="text-xs text-gray-500 mb-1">
						Comma-separated tool names to expose. Leave empty to expose every tool the server
						offers.
					</p>
					<input
						type="text"
						value={form.allowedTools}
						onChange={(e) => setField("allowedTools", e.target.value)}
						placeholder="search_issues, create_issue"
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>

				{/* Disable */}
				<div className="pt-1">
					<SwitchComponent
						title="Disabled"
						subTitle="Keep the configuration but stop connecting to this server"
						enabled={!!form.deactivate}
						onChange={(state) => setField("deactivate", state)}
					/>
				</div>
			</div>
		</Modal>
	);
}
